import * as React from "react";
import { CircularProgress, IconButton, styled } from "@mui/material";
import { SettingsTextInput } from "./settingsTextInput";
import { useEffect, useRef, useState } from "react";
import { doesAssetExist } from "fe-common/s3/useS3AssetsManager";
import CancelIcon from "@mui/icons-material/Cancel";
import PublishIcon from "@mui/icons-material/Publish";
import { CommonAssetUploader } from "../../../fileUploader/commonAssetUploader";

//region [[ Styles ]]

const ImageIconEditorView = styled("div")({
  display: "flex",
  flexFlow: "row",
});

const ImageWithUrl = styled("div")({
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  minWidth: "250px",
});

const DeleteIconContainer = styled("div")({
  position: "absolute",
  width: "100%",
  height: "100%",
  padding: "10px",
  display: "flex",
  flexFlow: "column",
  justifyContent: "center",
  alignItems: "center",
  zIndex: 1999,
  "& svg": {
    fill: "white",
    filter: "drop-shadow(3px 3px 3px rgba(0,0,0,.8))",
  },
});

const IconView = styled("div")({
  width: "60px",
  height: "60px",
  background: "#efefef",
  borderRadius: "8px",
  position: "relative",
  display: "flex",
  flexFlow: "column",
  alignItems: "center",
  justifyContent: "center",
  marginBottom: "6px",
  "& img": {
    width: "60px",
    maxHeight: "60px",
    borderRadius: "8px",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface ImageIconEditorProps {
  value: string;
  onChange: (url: string) => void;
  disabled?: boolean;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const generateRandomNumber = () => Math.random() * 1000000;
const getAppIcon = (appIcon) => `https://assets.kahun.com/appAssets/${appIcon}`;
//endregion [[ Functions ]]

export const ImageIconEditor = ({ value, ...props }: ImageIconEditorProps) => {
  const [iconExists, setIconExists] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showDeleteButton, setShowDeleteButton] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [randomFileName, setRandomFileName] = useState(generateRandomNumber());
  const timerRef = useRef<any>();

  useEffect(() => {
    setLoading(true);
    const assetUrl =
      value?.includes("http://") || value?.includes("https://")
        ? value
        : getAppIcon(value);
    doesAssetExist(assetUrl)
      .then(setIconExists)
      .finally(() => setLoading(false));
  }, [value]);

  const handleTextChanged = (e) => {
    props.onChange(e.target.value);
  };

  const showDeleteIfNeeded = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (loading) return;
    setShowDeleteButton(true);
  };

  const hideDeleteIfNeeded = () => {
    if (!timerRef.current) {
      timerRef.current = setTimeout(() => setShowDeleteButton(false), 300);
    }
  };

  const handleUploadImageClicked = () => {
    setRandomFileName(generateRandomNumber);
    setShowUploadDialog(true);
  };

  const handleUploadFinished = (success, file) => {
    console.log("Upload finished", success, file);
    setShowUploadDialog(false);
    if (success) {
      props.onChange(file);
    }
  };

  return (
    <ImageIconEditorView>
      <ImageWithUrl>
        <IconView
          onMouseEnter={showDeleteIfNeeded}
          onMouseLeave={hideDeleteIfNeeded}
        >
          {!loading && showDeleteButton && iconExists && (
            <DeleteIconContainer>
              <IconButton
                disabled={props.disabled}
                onClick={() => handleUploadFinished(true, "")}
              >
                <CancelIcon />
              </IconButton>
            </DeleteIconContainer>
          )}
          {loading && <CircularProgress />}
          {!loading && iconExists && (
            <img
              src={
                value.includes("http") || value.includes("https")
                  ? value
                  : getAppIcon(value)
              }
            />
          )}
          {!iconExists && (
            <IconButton
              onClick={handleUploadImageClicked}
              disabled={props.disabled}
            >
              <PublishIcon />
            </IconButton>
          )}
        </IconView>
        <SettingsTextInput
          value={value}
          onChange={handleTextChanged}
          disabled={props.disabled}
        />
      </ImageWithUrl>
      <CommonAssetUploader
        isOpen={showUploadDialog}
        onUploadFinished={handleUploadFinished}
        bucketFolder={"themeIcons"}
        fileName={randomFileName.toString()}
        assignExtensionByFile
      />
    </ImageIconEditorView>
  );
};
